import { apiRoutes } from '@/constants/api-routes';
import { clearStoredReferral, getStoredReferralCode } from '@/lib/referral';
import { apiRequest } from './http';
import type { AuthCallbackResult } from '@/types/api';

/**
 * Payload for `POST /auth/callback`. Must match the server's
 * `AuthCallbackDto` shape (see `server/src/modules/auth/dto/auth-callback.dto.ts`):
 * both `firstName` and `lastName` are required, 1–120 chars. On existing
 * accounts the server ignores these values; they're only applied on first
 * login when provisioning the `accounts` row.
 *
 * `referredByCode` is sourced from `lib/referral.ts` and only applied
 * server-side on first login — existing-account callbacks ignore it.
 *
 * `email` is forwarded from the Auth0 profile (`user.email`) so the server
 * can send the new-account welcome email — the API never persists it.
 * Optional and omitted when the profile has no email, so it never blocks
 * the callback.
 */
export interface AuthCallbackPayload {
  firstName: string;
  lastName: string;
  referredByCode?: string;
  email?: string;
}

/**
 * The only call in this service — `POST /auth/callback` — hits the server
 * with the Auth0 JWT attached. The server either provisions a new
 * `accounts` row (first login) or returns the existing one. Called once
 * inside `<ProtectedRoute />`, result is stashed in `AccountStore`.
 *
 * Referral is piggybacked here (rather than a separate endpoint) so a
 * race between bootstrap and "send the ref" can't drop attribution on
 * first signup. On `created: true` we evict the stored ref so a
 * different user on the same browser later doesn't get re-attributed.
 */
export const authService = {
  async callback(payload: AuthCallbackPayload): Promise<AuthCallbackResult> {
    const referredByCode = payload.referredByCode ?? getStoredReferralCode() ?? undefined;
    const result = await apiRequest<AuthCallbackResult>(apiRoutes.auth.callback, {
      method: 'POST',
      body: { ...payload, ...(referredByCode ? { referredByCode } : {}) },
    });
    if (result.created) {
      clearStoredReferral();
    }
    return result;
  },
};
